import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { SystemChipItem } from "../../../components/UIToolkit/Clickable/TabsAndChips/SystemChips/SystemChips";
import type { SystemAutoCompleteOption } from "../../../components/UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import { useAuth } from "../../../contexts/AuthProvider";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";
import { useDepartmentFlags } from "../../../hooks/useDepartmentFlags";
import type { AdminRole, Nurse } from "../../../utils/models";
import {
	clearGroupedActivityLogFilters,
	GROUPED_ACTIVITY_LOG_DATE_FORMAT,
	GROUPED_ACTIVITY_LOG_TIME_FORMAT,
	type GroupedActivityLogUrlState,
} from "./groupedActivityLogUrlState";
import { maskPerformedByValue } from "./useActivityLogPerformedByOptionsQuery";
import { type GroupedActivityLogFilterOptions, useGroupedActivityLogFilterOptions } from "./useGroupedActivityLogFilterOptions";
import { type UseGroupedActivityLogUrlStateResult, useGroupedActivityLogUrlState, type WriteOptions } from "./useGroupedActivityLogUrlState";

/** How long the search box waits after the last keystroke before writing `q` to the URL. The grid
 * filters on the typed text immediately; only the address bar waits. */
const SEARCH_URL_COMMIT_DEBOUNCE_MS = 300;

const PUSH: WriteOptions = { replace: false };
const REPLACE: WriteOptions = { replace: true };

type FilterSetters = {
	setActionTimeStart: (date: DateTime) => void;
	setActionTimeEnd: (date: DateTime) => void;
	setPerformedBy: (values: string[]) => void;
	setPerformedForNurse: (option: SystemAutoCompleteOption<Nurse> | null) => void;
	setAdminRoles: (values: AdminRole[]) => void;
	setActionNames: (values: string[]) => void;
	toggleChannel: (item: SystemChipItem<string>, selected: boolean) => void;
	setShiftDate: (date: DateTime | undefined) => void;
	setShiftStartTime: (value: DateTime | null) => void;
	setShiftEndTime: (value: DateTime | null) => void;
	setShiftJobIds: (values: string[]) => void;
	setExactShiftTimes: (checked: boolean) => void;
	setShowPunches: (checked: boolean) => void;
	clearAllFilters: () => void;
};

type FilterDateTimes = {
	/** Undefined only when the department's timezone is unusable (see `defaultRange`). */
	actionTimeStart: DateTime | undefined;
	actionTimeEnd: DateTime | undefined;
	shiftDate: DateTime | undefined;
	shiftStartTime: DateTime | null;
	shiftEndTime: DateTime | null;
};

export type UseGroupedActivityLogUrlFiltersResult = GroupedActivityLogFilterOptions &
	FilterDateTimes &
	FilterSetters & {
		performedBy: string[];
		performedForNurse: SystemAutoCompleteOption<Nurse> | null;
		/** False while the URL names a nurse but this department's roster hasn't loaded, so it can't yet
		 * say whether that nurse applies — the list must not be requested until it can. A failed roster
		 * counts as resolved (the nurse is not applied), so the list is never held forever. */
		performedForResolved: boolean;
		adminRoles: AdminRole[];
		actionNames: string[];
		selectedChannels: SystemChipItem<string>[];
		shiftJobIds: string[];
		exactShiftTimes: boolean;
		showPunches: boolean;
		/** What the search box shows and the grid filters on — ahead of the URL while typing. */
		search: string;
		/** Search-box typing: updates at once, reaches the URL after a short pause (replace). */
		setSearch: (value: string) => void;
		/** A one-off jump to a search value (e.g. "go to transaction") — pushes history at once. */
		jumpToSearch: (value: string) => void;
	};

const toCalendarDay = (date: DateTime): string => date.toFormat(GROUPED_ACTIVITY_LOG_DATE_FORMAT);
const toTimeOfDay = (value: DateTime | null): string | null => (value ? value.toFormat(GROUPED_ACTIVITY_LOG_TIME_FORMAT) : null);
const parseCalendarDay = (value: string, timezone: string): DateTime => DateTime.fromFormat(value, GROUPED_ACTIVITY_LOG_DATE_FORMAT, { zone: timezone });
/** The `q` the URL will hold for a typed value — a whitespace-only query is not written. */
const committedSearch = (value: string): string => (value.trim().length > 0 ? value : "");

/** A `HH:mm` value as a DateTime on `anchor`'s day — the pickers display a full DateTime, and
 * `composeShiftWindow` reads only its hour and minute. */
const parseTimeOfDay = (value: string | null, anchor: DateTime): DateTime | null => {
	if (value === null) return null;
	const time = DateTime.fromFormat(value, GROUPED_ACTIVITY_LOG_TIME_FORMAT);
	return anchor.set({ hour: time.hour, minute: time.minute, second: 0, millisecond: 0 });
};

/**
 * The search box keeps a local draft (like `TeamSearchBar`): binding the input straight to the URL
 * navigates on every keystroke, and the router's transition makes the controlled input lag — the
 * cursor jumps and fast typing drops characters. The draft commits to `q` after a pause, and re-syncs
 * when `q` changes from outside (Back/Forward) but not on its own echo.
 *
 * Re-syncing on every navigation (not only a `q` change) would also catch "Back to an entry with the
 * same `q`", but a filter click mid-typing is a navigation too, and re-syncing there would throw away
 * the characters typed since the last commit — so it is deliberately keyed on `q` alone.
 *
 * `jumpToSearch` lives here, not with the other setters, so it can take over the draft: it records its
 * value as committed and replaces the draft BEFORE pushing, so a debounced commit of what was being
 * typed can't land on top of the jump and wipe it.
 */
const useSearchDraft = (
	urlSearch: string,
	{ updateFilters, urlMatchesDepartment }: Pick<UseGroupedActivityLogUrlStateResult, "updateFilters" | "urlMatchesDepartment">,
) => {
	const [draft, setDraft] = useState(urlSearch);
	const draftRef = useRef(draft);
	draftRef.current = draft;
	const lastCommittedRef = useRef(urlSearch);
	useEffect(() => {
		if (urlSearch === lastCommittedRef.current) return;
		lastCommittedRef.current = urlSearch;
		setDraft(urlSearch);
	}, [urlSearch]);

	const debouncedDraft = useDebouncedValue(draft, SEARCH_URL_COMMIT_DEBOUNCE_MS);
	useEffect(() => {
		// A debounced value the draft has already moved past (a jump replaced it) is never committed.
		if (!urlMatchesDepartment || debouncedDraft !== draftRef.current) return;
		const next = committedSearch(debouncedDraft);
		if (next === lastCommittedRef.current) return;
		lastCommittedRef.current = next;
		updateFilters({ search: next }, REPLACE);
	}, [debouncedDraft, updateFilters, urlMatchesDepartment]);

	const jumpToSearch = useCallback(
		(value: string) => {
			lastCommittedRef.current = committedSearch(value);
			draftRef.current = value;
			setDraft(value);
			updateFilters({ search: value }, PUSH);
		},
		[updateFilters],
	);

	return { search: draft, setSearch: setDraft, jumpToSearch };
};

/** The URL's primitive values as department-zoned DateTimes, rebuilt only when those values change. */
const useFilterDateTimes = (urlState: GroupedActivityLogUrlState, from: string | null, to: string | null): FilterDateTimes => {
	const { timezone } = useDepartmentFlags();
	const actionTimeStart = useMemo(() => (from === null ? undefined : parseCalendarDay(from, timezone).startOf("day")), [from, timezone]);
	// Inclusive: `to` names the last day shown, so the window runs to the end of it.
	const actionTimeEnd = useMemo(() => (to === null ? undefined : parseCalendarDay(to, timezone).endOf("day")), [to, timezone]);
	const shiftDate = useMemo(
		() => (urlState.shiftDate === null ? undefined : parseCalendarDay(urlState.shiftDate, timezone).startOf("day")),
		[urlState.shiftDate, timezone],
	);
	// DEFAULT: times without a date (the date was cleared, or a hand-edited link) sit on today so the
	// pickers can display them; `composeShiftWindow` sends nothing without a date anyway.
	const timeAnchor = useMemo(() => shiftDate ?? DateTime.now().setZone(timezone).startOf("day"), [shiftDate, timezone]);
	const shiftStartTime = useMemo(() => parseTimeOfDay(urlState.shiftStartTime, timeAnchor), [urlState.shiftStartTime, timeAnchor]);
	const shiftEndTime = useMemo(() => parseTimeOfDay(urlState.shiftEndTime, timeAnchor), [urlState.shiftEndTime, timeAnchor]);
	return { actionTimeStart, actionTimeEnd, shiftDate, shiftStartTime, shiftEndTime };
};

const useFilterSetters = ({ updateFilters, commit }: Pick<UseGroupedActivityLogUrlStateResult, "updateFilters" | "commit">): FilterSetters =>
	useMemo(
		() => ({
			setActionTimeStart: (date) => updateFilters({ from: toCalendarDay(date) }, PUSH),
			setActionTimeEnd: (date) => updateFilters({ to: toCalendarDay(date) }, PUSH),
			setPerformedBy: (values) => updateFilters({ performedBy: values }, PUSH),
			setPerformedForNurse: (option) => updateFilters({ performedFor: option ? option.value.id : null }, PUSH),
			setAdminRoles: (values) => updateFilters({ adminRoles: values }, PUSH),
			setActionNames: (values) => updateFilters({ actionNames: values }, PUSH),
			// Computed from the params being written to, not this render's selection, so two quick clicks
			// before a re-render both land.
			toggleChannel: (item, selected) =>
				updateFilters((current) => {
					const others = current.channels.filter((value) => value !== item.value);
					return { channels: selected ? [...others, item.value] : others };
				}, PUSH),
			setShiftDate: (date) => updateFilters({ shiftDate: date ? toCalendarDay(date) : null }, PUSH),
			setShiftStartTime: (value) => updateFilters({ shiftStartTime: toTimeOfDay(value) }, REPLACE),
			setShiftEndTime: (value) => updateFilters({ shiftEndTime: toTimeOfDay(value) }, REPLACE),
			setShiftJobIds: (values) => updateFilters({ shiftJobIds: values }, PUSH),
			setExactShiftTimes: (checked) => updateFilters({ exactShiftTimes: checked }, PUSH),
			setShowPunches: (checked) => updateFilters({ showPunches: checked }, PUSH),
			clearAllFilters: () => commit(clearGroupedActivityLogFilters, PUSH),
		}),
		[updateFilters, commit],
	);

/**
 * Removes from the URL what the page can't apply, once the data to judge it has loaded — otherwise
 * every later write would carry it along and the invisible value would keep being re-shared:
 * - a `performed_for` id that isn't one of this department's nurses;
 * - a `shift_job` the department no longer has enabled;
 * - for a non-support viewer, a raw internal username (from a link a support user shared), rewritten
 *   to the bucket it masks to — so it can't show as a second "Support" option or stay in the link.
 *   Only values the fetched options don't carry are rewritten, and only once those options are in:
 *   the backend already masked every option it returned, and the frontend's email check is not the
 *   backend's (`maskPerformedByValue`), so re-masking a returned option could turn a real user the
 *   viewer picked into the "System" bucket.
 */
const usePruneUnresolvableFilters = (
	{ urlState, updateFilters, urlMatchesDepartment }: Pick<UseGroupedActivityLogUrlStateResult, "urlState" | "updateFilters" | "urlMatchesDepartment">,
	{
		departmentNurseIds,
		jobsLoaded,
		fetchedPerformedByValues,
	}: Pick<GroupedActivityLogFilterOptions, "departmentNurseIds" | "jobsLoaded" | "fetchedPerformedByValues">,
	resolvableShiftJobIds: string[],
): void => {
	const { userInfo } = useAuth();
	// DEFAULT: no user info yet reads as non-support — the side that masks, so nothing is revealed.
	const isSupport = !!userInfo?.support;
	const staleNurse = departmentNurseIds !== null && urlState.performedFor !== null && !departmentNurseIds.has(urlState.performedFor);
	const staleJobs = jobsLoaded && resolvableShiftJobIds.length !== urlState.shiftJobIds.length;
	const maskedPerformedBy = useMemo(
		() =>
			isSupport || fetchedPerformedByValues === null
				? urlState.performedBy
				: [...new Set(urlState.performedBy.map((value) => (fetchedPerformedByValues.has(value) ? value : maskPerformedByValue(value))))],
		[isSupport, fetchedPerformedByValues, urlState.performedBy],
	);
	const unmaskedPerformedBy =
		maskedPerformedBy.length !== urlState.performedBy.length || maskedPerformedBy.some((value, index) => value !== urlState.performedBy[index]);
	useEffect(() => {
		if (!urlMatchesDepartment || (!staleNurse && !staleJobs && !unmaskedPerformedBy)) return;
		updateFilters(
			{
				...(staleNurse ? { performedFor: null } : {}),
				...(staleJobs ? { shiftJobIds: resolvableShiftJobIds } : {}),
				...(unmaskedPerformedBy ? { performedBy: maskedPerformedBy } : {}),
			},
			REPLACE,
		);
	}, [urlMatchesDepartment, staleNurse, staleJobs, unmaskedPerformedBy, resolvableShiftJobIds, maskedPerformedBy, updateFilters]);
};

/**
 * Owns every Grouped Activity Log filter. The URL is the single source of truth
 * (`useGroupedActivityLogUrlState`); the option lists come from `useGroupedActivityLogFilterOptions`;
 * this hook converts between the two and the controls' value types.
 *
 * `storedShowPunches` is the user's saved Punches preference, used only when the URL doesn't say.
 */
export function useGroupedActivityLogUrlFilters({ storedShowPunches }: { storedShowPunches: boolean }): UseGroupedActivityLogUrlFiltersResult {
	const url = useGroupedActivityLogUrlState({ storedShowPunches });
	const { urlState } = url;
	const options = useGroupedActivityLogFilterOptions({ selectedPerformedBy: urlState.performedBy });
	const dateTimes = useFilterDateTimes(urlState, url.from, url.to);
	const { search, setSearch, jumpToSearch } = useSearchDraft(url.urlSearch, url);

	// A nurse id or job id this department can't resolve (a stale link, a retired job) has no option to
	// show it as, so it is not applied — and `usePruneUnresolvableFilters` takes it out of the URL. The
	// nurse is judged only once this department's roster has loaded (`departmentNurseIds` is null until then).
	const { performedFor } = urlState;
	const { departmentNurseIds, departmentNursesFailed, nurseOptions } = options;
	const performedForResolved = performedFor === null || departmentNurseIds !== null || departmentNursesFailed;
	const performedForNurse = useMemo(() => {
		if (performedFor === null || !departmentNurseIds?.has(performedFor)) return null;
		return nurseOptions.find((option) => option.value.id === performedFor) ?? null;
	}, [performedFor, departmentNurseIds, nurseOptions]);
	const shiftJobIds = useMemo(() => urlState.shiftJobIds.filter((id) => options.enabledJobIds.has(id)), [urlState.shiftJobIds, options.enabledJobIds]);
	usePruneUnresolvableFilters(url, options, shiftJobIds);

	const selectedChannels = useMemo(
		() => options.channelItems.filter((item) => urlState.channels.includes(item.value)),
		[options.channelItems, urlState.channels],
	);

	return {
		...options,
		...dateTimes,
		...useFilterSetters(url),
		performedBy: urlState.performedBy,
		performedForNurse,
		performedForResolved,
		adminRoles: urlState.adminRoles,
		actionNames: urlState.actionNames,
		selectedChannels,
		shiftJobIds,
		exactShiftTimes: urlState.exactShiftTimes,
		showPunches: urlState.showPunches,
		search,
		setSearch,
		jumpToSearch,
	};
}
